import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strapiGet, strapiPost, strapiPut } from '@/lib/strapi'
import { toast } from 'sonner'

export type Plan = 'free' | 'pro' | 'enterprise'
export type POSMode = 'integrated' | 'standalone' | 'hybrid'

export interface DeliveryZone {
  town: string
  aliases?: string[]
  postalCodes?: string[]
  deliveryFee: number | string | null
  minimumOrder: number | string | null
  center?: { latitude: number; longitude: number } | null
  radiusKm?: number | null
  polygon?: Array<{ latitude: number; longitude: number }>
}

export interface Restaurant {
  id: number
  documentId: string
  bookosId: string
  name: string
  slug: string
  email?: string
  phone?: string
  currency?: string
  languageCode?: string
  timezoneId?: string
  onboardingCompleted: boolean
  featureSheAnswers?: boolean
  featureCustomerPage?: boolean
  featurePOS?: boolean
  posMode?: POSMode
  acceptAsapOrders?: boolean
  asapAvailabilityMode?: 'schedule' | 'manual_on' | 'manual_off'
  asapManualUntil?: string | null
  deliveryChargePerKm?: number | string
  deliveryFlatFee?: number | string
  deliveryZones?: DeliveryZone[] | null
  onlinePaymentsEnabled?: boolean
  onlinePaymentsOptional?: boolean
  onlinePaymentProvider?: 'revolut'
  revolutEnvironment?: 'sandbox' | 'production'
  revolutProductionApiKey?: string
  revolutProductionPublicKey?: string
  revolutSandboxConfigured?: boolean
  revolutProductionConfigured?: boolean
  revolutSandboxPublicConfigured?: boolean
  revolutProductionPublicConfigured?: boolean
  elevenlabsAgentId?: string
  elevenlabsPhoneNumberId?: string
  sheAnswersPhoneNumber?: string
  elevenlabsKnowledgeBaseId?: string
  sheAnswersAllowBookings?: boolean
  sheAnswersAllowOrders?: boolean
  plan?: Plan
  sheAnswersTestCallsUsed?: number
  ticketLogo?: {
    id: number
    url: string
    formats?: Record<string, { url: string }>
  } | null
  address?: {
    address1?: string
    address2?: string
    address3?: string
    city?: string
    postalCode?: string
    countryCode?: string
    country?: string
    latitude?: number | string
    longitude?: number | string
  }
}

export interface UsageStat {
  used: number
  limit: number | null  // null = unlimited
}

export interface MyUsage {
  plan: Plan
  currentPeriod: { year: number; month: number }
  usage: {
    manualBookings: UsageStat
    customerPageBookings: UsageStat
    sheAnswersTestCalls: UsageStat
  }
  features: {
    phoneNumberAllowed: boolean
    whatsappAllowed: boolean
    sheAnswersMonthlyCallCap: number | null
    posEnabled: boolean
  }
}

export function useRestaurants() {
  return useQuery({
    queryKey: ['restaurants'],
    queryFn: () => strapiGet<Restaurant[]>('/api/restaurants', { populate: { address: true } }),
    staleTime: 10 * 60_000,
  })
}

export function useMyRestaurant() {
  return useQuery({
    queryKey: ['my-restaurant'],
    queryFn: () => strapiGet<Restaurant[]>('/api/restaurants/me', { populate: { address: true, ticketLogo: true } }),
    staleTime: 10 * 60_000,
  })
}

export function useCreateRestaurant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Restaurant> & { bookosId: string; name: string }) =>
      strapiPost<Restaurant>('/api/restaurants', { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['restaurants'] })
      qc.invalidateQueries({ queryKey: ['my-restaurant'] })
      toast.success('Restaurant created')
    },
    onError: () => toast.error('Failed to create restaurant'),
  })
}

export function useUpdateRestaurant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, ...data }: { documentId: string } & Partial<Restaurant>) =>
      strapiPut<Restaurant>(`/api/restaurants/${documentId}`, { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['restaurants'] })
      qc.invalidateQueries({ queryKey: ['my-restaurant'] })
    },
    onError: () => toast.error('Failed to update restaurant'),
  })
}

export function useMyUsage() {
  return useQuery({
    queryKey: ['my-restaurant-usage'],
    queryFn: () => strapiGet<MyUsage>('/api/restaurants/me/usage'),
    staleTime: 2 * 60_000,
  })
}
